import type { ChangeEvent, KeyboardEvent } from 'react';

// Shared across every Hours field (Work Log tab in IssueDetailDialog, quick-entry
// LogTimeDialog) — blocks the keys a browser-native type="number" input would otherwise accept
// as part of a negative or scientific-notation value (e.g. "-5", "1e5") before they ever reach
// state.
export function blockNegativeHourKeys(e: KeyboardEvent<HTMLInputElement>) {
  if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
}

// Rejects an edit outright (leaves state untouched) unless the resulting value is empty or a
// non-negative number — covers paste-in input that blockNegativeHourKeys' keydown guard can't
// catch, without stripping characters out from under the caret mid-edit.
export function nonNegativeHoursChange(setHours: (value: string) => void) {
  return (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '' || Number(raw) >= 0) setHours(raw);
  };
}

// The above can still leave a non-submittable value, e.g. a lone "." — catches that before it
// reaches `parseFloat()`/`Number()` in the mutation payload.
export function isValidHours(value: string): boolean {
  return value.trim().length > 0 && !Number.isNaN(Number(value)) && Number(value) >= 0;
}

// At most 2 integer digits and 2 decimal digits (e.g. "23.75") — a stricter variant of
// nonNegativeHoursChange for fields that also cap the value itself (e.g. WorkLogPanel's 24-hour
// daily max), so a user can't type past a sane digit count before the max-value check even runs.
const HOURS_FORMAT = /^\d{0,2}(\.\d{0,2})?$/;

export function restrictedHoursChange(setHours: (value: string) => void) {
  return (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '' || HOURS_FORMAT.test(raw)) setHours(raw);
  };
}

// Same digit-shape restriction as restrictedHoursChange, plus silently rejects any edit whose
// numeric value would exceed `max` — for fields that enforce the cap by refusing the keystroke
// rather than by showing an inline error (e.g. a single work log entry can't exceed a 24-hour
// day, but the Work Log tab doesn't surface a message for it).
export function cappedHoursChange(setHours: (value: string) => void, max: number) {
  return (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') { setHours(raw); return; }
    if (!HOURS_FORMAT.test(raw) || Number(raw) > max) return;
    setHours(raw);
  };
}
